import React, { useContext, lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import AuthContext from '../store/AuthContext'
import Profile from '../Pages/Profile';

export const PageLoader = () => (
    <div
        style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
        }}
    >
        <div
            style={{
                width: 24,
                height: 24,
                border: "2px solid #e5e5e5",
                borderTop: "2px solid #333",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
            }}
        />
        <style>
            {`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}
        </style>
    </div>
);

const ProtectedRoute = ({ children }) => {
    const { isLoggedIn } = useContext(AuthContext);
    if (!isLoggedIn) {
        // Redirect user to login and save the location they were trying to go to.
        return <Navigate to="/" replace />;
    }
    return children;
};


// CRITICAL: Each lazy import creates a separate chunk
// Use webpackChunkName to name them properly

// Landing pages - highest priority
const FindSalonJobs = lazy(() =>
    import(/* webpackChunkName: "find-salon-jobs" */ '../Pages/LandingPages/FindSalonJobs')
)
const Home = lazy(() =>
    import(/* webpackChunkName: "home" */ '../Pages/LandingPages/Home')
)

// Auth pages
const Login = lazy(() =>
    import(/* webpackChunkName: "login" */ '../Pages/Login')
)
const LoginForWorkshop = lazy(() =>
    import(/* webpackChunkName: "login-workshop" */ '../Pages/Authentication/LoginForWorkshop')
)

// Static pages - can be grouped
const SLCs = lazy(() =>
    import(/* webpackChunkName: "static-pages" */ '../Pages/SLCs')
)
const About = lazy(() =>
    import(/* webpackChunkName: "static-pages" */ '../Pages/About')
)
const PrivacyPolicy = lazy(() =>
    import(/* webpackChunkName: "static-pages" */ '../Pages/PrivacyPolicy')
)
const TermsOfUse = lazy(() =>
    import(/* webpackChunkName: "static-pages" */ '../Pages/TermsOfUse')
)
const RefundPolicy = lazy(() =>
    import(/* webpackChunkName: "static-pages" */ '../Pages/RefundPolicy')
)
const OurPresence = lazy(() =>
    import(/* webpackChunkName: "static-pages" */ '../Pages/OurPresence')
)

// Course pages
const Courses = lazy(() =>
    import(/* webpackChunkName: "courses" */ '../Pages/Courses')
)
const PremiumCategory = lazy(() =>
    import(/* webpackChunkName: "courses" */ '../Courses/PremiumCategory')
)
const PremiumAsset = lazy(() =>
    import(/* webpackChunkName: "courses" */ '../Courses/PremiumAsset')
)
const SearchAmongCourses = lazy(() =>
    import(/* webpackChunkName: "courses" */ '../Courses/SearchAmongCourses')
)

// Jobs pages
const JobsListing = lazy(() =>
    import(/* webpackChunkName: "jobs" */ '../Jobs/JobsListing')
)
const EachJob = lazy(() =>
    import(/* webpackChunkName: "jobs" */ '../Jobs/EachJob')
)
const JobsListingCategorised = lazy(() =>
    import(/* webpackChunkName: "jobs" */ '../Pages/LandingPages/JobsListingCategorised')
)

// Workshop pages
const Workshops = lazy(() =>
    import(/* webpackChunkName: "workshops" */ '../Pages/Workshops')
)
const PremiumAssestsWorkshops = lazy(() =>
    import(/* webpackChunkName: "workshops" */ '../Pages/Workshops/PremiumAssestsWorkshops')
)
const RegisterForWorkshop = lazy(() =>
    import(/* webpackChunkName: "workshops" */ '../Pages/Workshops/RegisterForWorkshop')
)
const RegisterWorkshopPaymentStatus = lazy(() =>
    import(/* webpackChunkName: "workshops" */ '../Pages/Workshops/RegisterWorkshopPaymentStatus')
)

// Certificates - separate chunk as they're rarely accessed
const Verify = lazy(() =>
    import(/* webpackChunkName: "certificates" */ '../Certificates/Verify')
)
const GCWCertificateAdmittance = lazy(() =>
    import(/* webpackChunkName: "certificates" */ '../Components/Workshops/GCWCertificateAdmittance')
)
const GCWCertificateAssessmentCover = lazy(() =>
    import(/* webpackChunkName: "certificates" */ '../Components/Workshops/GCWCertificateAssessmentCover')
)
const GCWCertificateAssessmentExam = lazy(() =>
    import(/* webpackChunkName: "certificates" */ '../Components/Workshops/GCWCertificateAssessmentExam')
)
const GCWCertificateAssessmentResult = lazy(() =>
    import(/* webpackChunkName: "certificates" */ '../Components/Workshops/GCWCertificateAssessmentResult')
)
const GCWCertificate = lazy(() =>
    import(/* webpackChunkName: "certificates" */ '../Components/Workshops/GCWCertificate')
)

// Community & Training
const Trainings = lazy(() =>
    import(/* webpackChunkName: "training" */ '../Pages/Trainings')
)
const Community = lazy(() =>
    import(/* webpackChunkName: "community" */ '../Pages/Community')
)
const TrainYourStaff = lazy(() =>
    import(/* webpackChunkName: "training" */ '../Pages/TrainYourStaff')
)

// Business pages
const HireSalonStaffHome = lazy(() =>
    import(/* webpackChunkName: "business" */ '../Pages/ForBusinesses/HireSalonStaffHome')
)
const HireSalonStaffQuickly = lazy(() =>
    import(/* webpackChunkName: "business" */ '../Pages/ForBusinesses/HireSalonStaffQuickly')
)
const SubscriptionsPricing = lazy(() =>
    import(/* webpackChunkName: "business" */ '../Pages/ForBusinesses/SubscriptionsPricing')
)
const ForSalon = lazy(() =>
    import(/* webpackChunkName: "business" */ '../Pages/LandingPages/ForSalon')
)
const Resume = lazy(() =>
    import(/* webpackChunkName: "business" */ '../Pages/LandingPages/Resume')
)

// Classroom pages
const ClassroomHome = lazy(() =>
    import(/* webpackChunkName: "classroom" */ '../Pages/Classroom/ClassroomHome')
)
const MakeupPromotionalLandingPage = lazy(() =>
    import(/* webpackChunkName: "classroom" */ '../Pages/Classroom/PromotionalLandingPages/MakeupPromotionalLandingPage')
)
const CHCourseEnquiryForm = lazy(() =>
    import(/* webpackChunkName: "classroom" */ '../Pages/Classroom/CHCourseEnquiryForm')
)
const HairPLP = lazy(() =>
    import(/* webpackChunkName: "classroom" */ '../Pages/Classroom/PromotionalLandingPages/HairPLP')
)

// Brand partners & Recruitment
const BPProducts = lazy(() =>
    import(/* webpackChunkName: "brand-partners" */ '../Pages/BrandPartners/BPProducts')
)
const InterviewDetails = lazy(() =>
    import(/* webpackChunkName: "recruitment" */ '../Pages/Recruitment/InterviewDetails')
)

const MainNavigator = () => {
    const authCtx = useContext(AuthContext)
    const isLoggedIn = authCtx.isLoggedIn

    return (
        <Suspense fallback={<PageLoader />}>
            <Routes>
                {/* <Route path="/login" element={isLoggedIn ? <Login /> : <LoginForWorkshop />} /> */}
                <Route path="/login" element={isLoggedIn ? <Navigate to="/" replace /> : <Login />} />
                <Route path="/loginforworkshop" element={<LoginForWorkshop />} />
                <Route
                    path="/profile"
                    element={
                        <ProtectedRoute>
                            <Profile />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/profile/appliedjobs"
                    element={
                        <ProtectedRoute>
                            <Profile />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/profile/savedjobs"
                    element={
                        <ProtectedRoute>
                            <Profile />
                        </ProtectedRoute>
                    }
                />

                {/* Landing pages */}
                <Route path="*" element={<FindSalonJobs />} />
                <Route path="/" element={<FindSalonJobs />} />
                <Route path="/home" element={<Home />} />
                <Route path="/find-salon-jobs" element={<FindSalonJobs />} />

                {/* Static pages */}
                <Route path="/slc" element={<SLCs />} />
                <Route path="/about" element={<About />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-use" element={<TermsOfUse />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/our-presence" element={<OurPresence />} />

                {/* Courses */}
                <Route path="/courses" element={<Courses />} />
                <Route path="/online-courses/:category" element={<PremiumCategory />} />
                <Route path="/online-course/:category/:identifier" element={<PremiumAsset />} />
                <Route path="/online-courses/search" element={<SearchAmongCourses />} />

                {/* Jobs */}
                <Route path="/jobs" element={<JobsListing />} />
                <Route path="/job/:route" element={<EachJob />} />
                <Route path="/job/:route/:trackIdentifier" element={<EachJob />} />
                <Route path="/:routeIdentifier" element={<JobsListingCategorised />} />

                {/* Certificates */}
                <Route path="/certificate/verify/:certificateIdentifier" element={<Verify />} />

                {/* Workshops */}
                <Route path="/workshops" element={<Workshops />} />
                <Route path="/workshop/:identifier" element={<PremiumAssestsWorkshops />} />
                <Route path="/workshop/:workshopIdentifier/register" element={<RegisterForWorkshop />} />
                {isLoggedIn && (
                    <Route path="/workshop/:workshopIdentifier/payment-status" element={<RegisterWorkshopPaymentStatus />} />
                )}
                <Route path="/workshop/certificate/admittance/:identifier" element={<GCWCertificateAdmittance />} />
                <Route path="/workshop/certificate/assessment/:identifier/cover" element={<GCWCertificateAssessmentCover />} />
                <Route path="/workshop/certificate/assessment/:identifier/exam" element={<GCWCertificateAssessmentExam />} />
                <Route path="/workshop/certificate/assessment/:identifier/result" element={<GCWCertificateAssessmentResult />} />
                <Route path="/workshop/certificate/:identifier" element={<GCWCertificate />} />

                {/* Training & Community */}
                <Route path="/training" element={<Trainings />} />
                <Route path="/community" element={<Community />} />
                <Route path="/train-your-salon-staff" element={<TrainYourStaff />} />

                {/* Business pages */}
                <Route path="/hire-salon-staff" element={<HireSalonStaffHome />} />
                <Route path="/hire-salon-staff/quickly" element={<HireSalonStaffQuickly />} />
                <Route path="/hire-salon-staff/pricing" element={<SubscriptionsPricing />} />
                <Route path="/solutions-for-businesses" element={<ForSalon />} />
                <Route path="/get-my-salon-resume" element={<Resume />} />

                {/* Classroom */}
                <Route path="/classroom-courses" element={<ClassroomHome />} />
                <Route path="/classroom-courses/make-up-courses" element={<MakeupPromotionalLandingPage />} />
                <Route path="/classroom-courses/:categoryIdentifier/enroll-now" element={<CHCourseEnquiryForm />} />
                <Route path="/classroom-courses/hair-dresser-courses" element={<HairPLP />} />

                {/* Brand partners & Recruitment */}
                <Route path="/brand-partners/:category" element={<BPProducts />} />
                <Route path="/recruitment/interview-details/:identifier" element={<InterviewDetails />} />
            </Routes>
        </Suspense>
    )
}

export default MainNavigator